<template>
	<div>
		<JuniperPageHeader v-if="isGlobal" back="/juniper" title="Logs" subtitle="Activity logs for every client · refreshes every 30 seconds" />
		<JuniperPageHeader v-else :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Activities" />

		<!-- Toolbar: action pills + subject select + date range + search -->
		<div class="act-toolbar">
			<div class="act-pills">
				<button
					v-for="opt in actionOptions"
					:key="opt.value"
					type="button"
					class="act-pill"
					:class="{ active: filterEvent === opt.value }"
					@click="setFilter('filterEvent', opt.value)"
				>
					{{ opt.label }}
				</button>
			</div>

			<div class="act-toolbar__right">
				<select v-model="filterSubject" class="act-select" @change="applyFilters">
					<option value="">All subjects</option>
					<option v-for="st in subjectTypes" :key="st.id" :value="st.id">{{ st.name }}</option>
				</select>
				<input type="date" class="act-date" v-model="dateFrom" title="From" @change="applyFilters" />
				<span class="act-date-sep">–</span>
				<input type="date" class="act-date" v-model="dateTo" title="To" @change="applyFilters" />
				<div class="act-search">
					<font-awesome-icon :icon="['fas', 'search']" class="act-search__icon" />
					<input
						type="text"
						class="act-search__input"
						placeholder="Search (Enter)"
						v-model.trim="search"
						@keyup.enter="applyFilters"
					/>
					<button v-if="search" type="button" class="act-search__clear" aria-label="Clear search" @click="clearSearch">
						<font-awesome-icon :icon="['fas', 'times']" />
					</button>
				</div>
				<button v-if="hasActiveFilters" class="act-icon-btn act-icon-btn--danger" title="Clear all filters" @click="clearFilters">
					<font-awesome-icon :icon="['fas', 'eraser']" />
				</button>
				<button class="act-icon-btn" title="Export CSV" @click="exportActivities">
					<font-awesome-icon :icon="['fas', 'file-csv']" />
				</button>
				<button v-if="isGlobal" class="act-icon-btn" title="Log settings" @click="openSettings">
					<font-awesome-icon :icon="['fas', 'cog']" />
				</button>
			</div>
		</div>

		<!-- Table -->
		<div class="ada-card">
			<div v-if="loading && activities.length === 0" class="act-empty">
				<span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading…
			</div>
			<div v-else-if="!loading && activities.length === 0" class="act-empty">No activities found.</div>

			<template v-else>
				<div class="act-table-wrap">
					<div v-if="loading" class="act-loading-overlay">
						<span class="spinner-border spinner-border-sm me-2" role="status"></span>
						Loading…
					</div>
					<div :class="{ 'act-table-blurred': loading }">
						<table class="act-table">
							<thead>
								<tr>
									<th class="act-col-id">ID</th>
									<th>Event</th>
									<th v-if="isGlobal">Client</th>
									<th>User</th>
									<th>IP</th>
									<th>Device</th>
									<th>Subject</th>
									<th>Date</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="activity in activities" :key="activity.id" class="act-row" @click="showActivityDetails(activity)">
									<td class="act-col-id act-muted">#{{ activity.id }}</td>
									<td>
										<span class="act-badge" :class="actionClass(activity.event)">{{ activity.event }}</span>
									</td>
									<td v-if="isGlobal" class="act-client">
										<span v-if="activity.client">{{ activity.client.name }}</span>
										<span v-else class="act-muted">—</span>
									</td>
									<td>
										<template v-if="activity.causer">
											<span class="act-user">{{ activity.causer.name }}</span>
											<span class="act-muted act-email">{{ activity.causer.email }}</span>
										</template>
										<span v-else class="act-muted">System</span>
									</td>
									<td class="act-muted act-origin">{{ ipLabel(activity) }}</td>
									<td class="act-muted act-origin" :title="activity.user_agent">{{ deviceLabel(activity.device) || '—' }}</td>
									<td>
										{{ getSubjectTypeName(activity.subject_type) }}
										<span class="act-subject-id">#{{ activity.subject_id }}</span>
									</td>
									<td class="act-muted" :title="getRelativeTime(activity.created_at)">{{ formatDate(activity.created_at) }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<div class="act-pagination">
					<span class="act-muted act-pagination__info">{{ rangeLabel }}</span>
					<div class="act-pagination__nav">
						<button
							type="button"
							class="act-page-link act-page-link--arrow"
							:disabled="loading || currentPage <= 1"
							title="Previous page"
							@click="goToPage(currentPage - 1)"
						>
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</button>
						<template v-for="(p, i) in pages">
							<span v-if="p === '...'" :key="'dots-' + i" class="act-page-dots">…</span>
							<button
								v-else
								:key="'p-' + p"
								type="button"
								class="act-page-link"
								:class="{ active: p === currentPage }"
								:disabled="loading"
								@click="goToPage(p)"
							>
								{{ p }}
							</button>
						</template>
						<button
							type="button"
							class="act-page-link act-page-link--arrow"
							:disabled="loading || !hasMore"
							title="Next page"
							@click="goToPage(currentPage + 1)"
						>
							<font-awesome-icon :icon="['fas', 'chevron-right']" />
						</button>
					</div>
				</div>
			</template>
		</div>

		<!-- Log settings (Logs page only): retention + empty-log cleanup -->
		<div v-if="settings.open" class="jdbg-modal-backdrop" @click.self="closeSettings">
			<div class="jdbg-modal act-settings">
				<div class="jdbg-modal-head">
					<b>Log settings</b>
					<button class="btn-close" @click="closeSettings"></button>
				</div>
				<div class="act-settings__body">
					<div class="act-settings__section">
						<div class="act-settings__title">Retention</div>
						<p class="act-muted act-settings__help">
							Activity logs older than this are deleted every night at 04:45, for every client.
						</p>
						<div class="act-settings__row">
							<select v-model.number="settings.retentionDays" class="act-select">
								<option v-for="opt in retentionOptions" :key="opt.days" :value="opt.days">{{ opt.label }}</option>
							</select>
							<button class="act-btn" :disabled="settings.saving" @click="saveRetention">
								<font-awesome-icon :icon="['fas', settings.saving ? 'spinner' : 'save']" :spin="settings.saving" />
								{{ settings.saving ? 'Saving…' : 'Save' }}
							</button>
						</div>
					</div>

					<div class="act-settings__section">
						<div class="act-settings__title">Empty logs</div>
						<p class="act-muted act-settings__help">
							"Updated" entries with no tracked changes — recorded when a form re-saved values that did
							not actually change ("No changes detected in tracked fields"). New ones are no longer written.
						</p>
						<div class="act-settings__row">
							<span v-if="settings.emptyCount === null" class="act-muted">
								<span class="spinner-border spinner-border-sm me-1" role="status"></span>Counting…
							</span>
							<span v-else><b>{{ settings.emptyCount.toLocaleString() }}</b> empty {{ settings.emptyCount === 1 ? 'log' : 'logs' }}</span>
							<template v-if="settings.emptyCount">
								<button v-if="!settings.confirmDelete" class="act-btn act-btn--danger" @click="settings.confirmDelete = true">
									<font-awesome-icon :icon="['fas', 'trash']" /> Delete
								</button>
								<template v-else>
									<button class="act-btn act-btn--danger" :disabled="settings.deleting" @click="deleteEmptyLogs">
										<font-awesome-icon :icon="['fas', settings.deleting ? 'spinner' : 'trash']" :spin="settings.deleting" />
										{{ settings.deleting ? 'Deleting…' : 'Yes, delete ' + settings.emptyCount.toLocaleString() }}
									</button>
									<button class="act-btn" :disabled="settings.deleting" @click="settings.confirmDelete = false">Cancel</button>
								</template>
							</template>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Details modal (jdbg style — replaces the old offcanvas) -->
		<div v-if="details.open" class="jdbg-modal-backdrop" @click.self="closeDetails">
			<div class="jdbg-modal">
				<div class="jdbg-modal-head">
					<div>
						<span class="event-badge me-2" :class="selectedActivity.event">{{ selectedActivity.event }}</span>
						<b>{{ getSubjectTypeName(selectedActivity.subject_type) }} #{{ selectedActivity.subject_id }}</b>
						<div class="jdbg-modal-sub">
							#{{ selectedActivity.id }} · {{ formatDate(selectedActivity.created_at) }}
							<span v-if="selectedActivity.causer"> · {{ selectedActivity.causer.name }} ({{ selectedActivity.causer.email }})</span>
							<span v-else> · System</span>
							<span v-if="originLabel(selectedActivity)"> · {{ originLabel(selectedActivity) }}</span>
							<div v-if="selectedActivity.user_agent" class="act-origin-ua">{{ selectedActivity.user_agent }}</div>
						</div>
					</div>
					<button class="btn-close" @click="closeDetails"></button>
				</div>

				<div class="jdbg-modal-body">
					<template v-if="selectedActivity.properties">
						<!-- Option Context Banner -->
						<div v-if="selectedActivity.subject_type === 'App\\Models\\Option' && getOptionContext(selectedActivity)" class="option-context">
							<font-awesome-icon :icon="['fas', 'cog']" class="me-2" />
							<strong>Setting changed:</strong>&nbsp;{{ getOptionContext(selectedActivity) }}
						</div>

						<!-- Created Event -->
						<template v-if="selectedActivity.event === 'created' && selectedActivity.properties.attributes">
							<h6 class="jdbg-section-title">New record</h6>
							<div v-for="(value, key) in selectedActivity.properties.attributes" :key="key" class="property-row">
								<div class="property-key">{{ formatPropertyKey(key) }}</div>
								<pre class="property-value property-value--new">{{ formatPropertyValue(value, key, selectedActivity) }}</pre>
							</div>
						</template>

						<!-- Updated Event -->
						<template v-if="selectedActivity.event === 'updated'">
							<h6 class="jdbg-section-title">Changes</h6>
							<template v-if="hasChanges(selectedActivity.properties)">
								<div v-for="key in getChangedKeys(selectedActivity.properties)" :key="key" class="property-row">
									<div class="property-key">{{ formatPropertyKey(key) }}</div>

									<!-- JSON values: show only what changed INSIDE the json, highlighted per path -->
									<div v-if="jsonDiffs[key]" class="json-diff">
										<div v-for="(d, i) in jsonDiffs[key]" :key="i" class="diff-row">
											<div class="diff-path">
												<span class="diff-type-badge" :class="d.type">{{ d.type }}</span>
												<code>{{ d.path || '(root)' }}</code>
											</div>
											<div v-if="d.type === 'changed'" class="change-comparison">
												<pre class="property-value property-value--old">{{ stringifyDiffValue(d.oldValue) }}</pre>
												<span class="change-arrow">
													<font-awesome-icon :icon="['fas', 'chevron-right']" />
												</span>
												<pre class="property-value property-value--new">{{ stringifyDiffValue(d.newValue) }}</pre>
											</div>
											<pre v-else-if="d.type === 'added'" class="property-value property-value--new">{{ stringifyDiffValue(d.newValue) }}</pre>
											<pre v-else class="property-value property-value--old">{{ stringifyDiffValue(d.oldValue) }}</pre>
										</div>
									</div>

									<!-- text values: inline word diff — only added/removed words highlighted -->
									<div v-else-if="textDiffs[key]">
										<div v-if="textDiffs[key].text || textDiffs[key].textIdentical" class="diff-mode-toggle">
											<button
												v-if="textDiffs[key].text"
												class="diff-mode-btn"
												:class="{ active: textDiffs[key].mode === 'text' }"
												@click="textDiffs[key].mode = 'text'"
											>Text</button>
											<button
												class="diff-mode-btn"
												:class="{ active: textDiffs[key].mode === 'html' }"
												@click="textDiffs[key].mode = 'html'"
											>HTML</button>
											<span v-if="textDiffs[key].textIdentical" class="cell-muted diff-mode-note">visible text unchanged — markup-only change</span>
										</div>
										<div class="change-comparison">
											<pre class="property-value property-value--old" v-html="activeDiff(key).oldHtml"></pre>
											<span class="change-arrow">
												<font-awesome-icon :icon="['fas', 'chevron-right']" />
											</span>
											<pre class="property-value property-value--new" v-html="activeDiff(key).newHtml"></pre>
										</div>
									</div>

									<!-- scalar values: classic before → after -->
									<div v-else class="change-comparison">
										<pre class="property-value property-value--old">{{ formatPropertyValue(selectedActivity.properties.old[key], key, selectedActivity) }}</pre>
										<span class="change-arrow">
											<font-awesome-icon :icon="['fas', 'chevron-right']" />
										</span>
										<pre class="property-value property-value--new">{{ formatPropertyValue(selectedActivity.properties.attributes[key], key, selectedActivity) }}</pre>
									</div>
								</div>
							</template>
							<p v-else class="cell-muted">No changes detected in tracked fields</p>
						</template>

						<!-- Deleted Event -->
						<template v-if="selectedActivity.event === 'deleted' && selectedActivity.properties.attributes">
							<h6 class="jdbg-section-title">Deleted record</h6>
							<div v-for="(value, key) in selectedActivity.properties.attributes" :key="key" class="property-row">
								<div class="property-key">{{ formatPropertyKey(key) }}</div>
								<pre class="property-value property-value--old">{{ formatPropertyValue(value, key, selectedActivity) }}</pre>
							</div>
						</template>

					</template>
					<p v-else class="cell-muted">No properties recorded for this activity.</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import { showGlobalToast } from '@/utils/toast'

// Logs page (all clients) silently re-fetches the current page this often.
const REFRESH_MS = 30000

export default {
	name: 'ClientActivities',
	data() {
		return {
			loading: false,
			client: {},
			activities: [],
			selectedActivity: null,
			details: { open: false },
			jsonDiffs: {},
			textDiffs: {},
			params: {},
			page: 1,
			filterEvent: '',
			filterSubject: '',
			dateFrom: '',
			dateTo: '',
			search: '',
			subjectTypes: [],
			refreshTimer: null,
			settings: {
				open: false,
				retentionDays: 365,
				saving: false,
				emptyCount: null,
				confirmDelete: false,
				deleting: false,
			},
			retentionOptions: [
				{ days: 30, label: '30 days' },
				{ days: 60, label: '60 days' },
				{ days: 90, label: '90 days' },
				{ days: 180, label: '180 days' },
				{ days: 365, label: '1 year (365 days)' },
				{ days: 730, label: '2 years (730 days)' },
			],
			actionOptions: [
				{ value: '', label: 'All' },
				{ value: 'created', label: 'Created' },
				{ value: 'updated', label: 'Updated' },
				{ value: 'deleted', label: 'Deleted' } ] }
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		// Same component serves a client's Activities and the all-clients
		// Juniper → Logs page (route without :id).
		isGlobal() {
			return !this.$route.params.id
		},
		apiBase() {
			return this.isGlobal ? '/activities' : '/activities/client/' + this.$route.params.id
		},
		hasActiveFilters() {
			return !!(this.filterEvent || this.filterSubject || this.dateFrom || this.dateTo || this.search)
		},
		currentPage() {
			return this.params.current_page || this.page || 1
		},
		lastPage() {
			return this.params.last_page || 1
		},
		perPage() {
			return this.params.per_page || 20
		},
		hasMore() {
			return this.currentPage < this.lastPage
		},
		rangeLabel() {
			const total = this.params.total || 0
			if (!total) return '0 of 0'
			const from = (this.currentPage - 1) * this.perPage + 1
			const to = Math.min(this.currentPage * this.perPage, total)
			return `${from}–${to} of ${total}`
		},
		// Windowed page list with ellipsis: 1 … 44 45 46 47 48 … 2000
		pages() {
			const last = this.lastPage
			const cur = this.currentPage
			const delta = 2
			const out = []
			let prev = 0
			for (let i = 1; i <= last; i++) {
				if (i === 1 || i === last || (i >= cur - delta && i <= cur + delta)) {
					if (prev && i - prev > 1) out.push('...')
					out.push(i)
					prev = i
				}
			}
			return out
		} },

	created() {
		this.init()
	},

	// Client Activities ↔ Logs share this component, so the router reuses the
	// instance when navigating between them — start over for the new route.
	watch: {
		'$route.params.id'() {
			this.clearFilters(false)
			this.activities = []
			this.params = {}
			this.client = {}
			this.init()
		},
	},

	beforeDestroy() {
		clearInterval(this.refreshTimer)
	},

	methods: {
		init() {
			clearInterval(this.refreshTimer)
			this.refreshTimer = null
			if (this.isGlobal) {
				this.getActivities()
				this.refreshTimer = setInterval(this.refreshSilently, REFRESH_MS)
			} else {
				this.getClient()
			}
			this.loadSubjectTypes()
		},

		// "Chrome 130 · macOS · desktop" from the API's parsed user agent
		deviceLabel(device) {
			if (!device) return ''
			return [device.browser, device.os, device.type].filter(Boolean).join(' · ')
		},

		// IP column: the visitor's address for HTTP requests (so a "System"
		// unsubscribe shows who triggered it), else the runner, else a dash.
		ipLabel(activity) {
			if (activity.source === 'request') return activity.ip_address || '—'
			return { console: 'Console / cron', queue: 'Queued job', scheduler: 'Scheduler' }[activity.source] || '—'
		},

		// Where the activity came from: IP + device for HTTP requests (so a
		// "System" unsubscribe shows the visitor's address), else the runner.
		originLabel(activity) {
			if (activity.source === 'request') {
				return [activity.ip_address, this.deviceLabel(activity.device)].filter(Boolean).join(' · ')
			}
			return { console: 'Console / cron', queue: 'Queued job', scheduler: 'Scheduler' }[activity.source] || ''
		},

		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.loading = false
					this.getActivities()
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		loadSubjectTypes() {
			juniperApi
				.get(this.apiBase + '/subject-types')
				.then(({ data }) => {
					this.subjectTypes = data || []
				})
				.catch(() => {
					this.subjectTypes = []
				})
		},

		buildFilterParams() {
			const params = {}
			if (this.filterEvent) params.event = this.filterEvent
			if (this.filterSubject) params.subject_type = this.filterSubject
			if (this.dateFrom) params.date_from = this.dateFrom
			if (this.dateTo) params.date_to = this.dateTo
			if (this.search) params.search = this.search
			return params
		},

		getActivities() {
			this.loading = true
			const params = { page: this.page, ...this.buildFilterParams() }

			juniperApi
				.get(this.apiBase, { params })
				.then(({ data }) => {
					this.activities = data.data
					this.params = data
					delete this.params.data
					this.loading = false
				})
				.catch(error => {
					this.activities = []
					this.loading = false
					console.log(error)
				})
		},

		// Auto-refresh tick: same page + filters, no loading blur, and skipped
		// while a request is already in flight or a modal is open.
		refreshSilently() {
			if (this.loading || this.details.open || this.settings.open || document.hidden) return
			const params = { page: this.page, ...this.buildFilterParams() }
			juniperApi
				.get(this.apiBase, { params })
				.then(({ data }) => {
					this.activities = data.data
					this.params = data
					delete this.params.data
				})
				.catch(error => console.log(error))
		},

		// ----- Log settings (Logs page) -----
		openSettings() {
			this.settings.open = true
			this.settings.confirmDelete = false
			this.settings.emptyCount = null
			juniperApi
				.get('/settings/retention_activity_logs_days')
				.then(({ data }) => {
					this.settings.retentionDays = Number(data?.value) || 365
				})
				.catch(() => {})
			this.loadEmptyCount()
		},
		closeSettings() {
			this.settings.open = false
		},
		loadEmptyCount() {
			juniperApi
				.get('/activities/empty')
				.then(({ data }) => {
					this.settings.emptyCount = data.count || 0
				})
				.catch(error => {
					this.settings.emptyCount = 0
					console.log(error)
				})
		},
		async saveRetention() {
			this.settings.saving = true
			try {
				await juniperApi.put('/settings/retention_activity_logs_days', { value: this.settings.retentionDays })
				showGlobalToast('Retention saved', 'success')
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save retention', 'danger')
			} finally {
				this.settings.saving = false
			}
		},
		async deleteEmptyLogs() {
			this.settings.deleting = true
			try {
				const { data } = await juniperApi.delete('/activities/empty')
				const n = data.deleted || 0
				showGlobalToast(`Deleted ${n.toLocaleString()} empty ${n === 1 ? 'log' : 'logs'}`, 'success')
				this.settings.confirmDelete = false
				this.loadEmptyCount()
				this.getActivities()
			} catch (e) {
				console.error(e)
				showGlobalToast(e?.response?.data?.message || 'Failed to delete empty logs', 'danger')
			} finally {
				this.settings.deleting = false
			}
		},

		setFilter(key, value) {
			this[key] = value
			this.page = 1
			this.getActivities()
		},

		applyFilters() {
			this.page = 1
			this.getActivities()
		},

		clearSearch() {
			this.search = ''
			this.applyFilters()
		},

		clearFilters(reload = true) {
			this.filterEvent = ''
			this.filterSubject = ''
			this.dateFrom = ''
			this.dateTo = ''
			this.search = ''
			this.page = 1
			if (reload) this.getActivities()
		},

		showActivityDetails(activity) {
			this.selectedActivity = activity
			this.jsonDiffs = this.buildJsonDiffs(activity)
			this.textDiffs = this.buildTextDiffs(activity)
			this.details.open = true
		},

		closeDetails() {
			this.details.open = false
		},

		// ── JSON diff ─────────────────────────────────────────────────
		// For updated events whose old/new values are JSON (objects or
		// serialized strings), compute a deep diff so the modal shows ONLY
		// the paths that changed inside the json, not the whole blob.
		buildJsonDiffs(activity) {
			const out = {}
			const p = activity.properties
			if (activity.event !== 'updated' || !this.hasChanges(p)) return out

			for (const key of this.getChangedKeys(p)) {
				const oldParsed = this.tryParseJson(p.old[key])
				const newParsed = this.tryParseJson(p.attributes[key])
				const bothJson =
					oldParsed !== undefined && newParsed !== undefined &&
					typeof oldParsed === 'object' && oldParsed !== null &&
					typeof newParsed === 'object' && newParsed !== null
				if (bothJson) {
					const rows = []
					this.deepDiff(oldParsed, newParsed, '', rows)
					if (rows.length) out[key] = rows
				}
			}
			return out
		},

		tryParseJson(value) {
			if (value === null || value === undefined) return undefined
			if (typeof value === 'object') return value
			if (typeof value === 'string') {
				const trimmed = value.trim()
				if (/^[[{]/.test(trimmed)) {
					try {
						return JSON.parse(trimmed)
					} catch (e) {
						return undefined
					}
				}
			}
			return undefined
		},

		deepDiff(oldVal, newVal, path, out) {
			const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v)

			if (isPlainObject(oldVal) && isPlainObject(newVal)) {
				const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)])
				for (const k of keys) {
					const p = path ? path + '.' + k : k
					if (!(k in oldVal)) out.push({ path: p, type: 'added', newValue: newVal[k] })
					else if (!(k in newVal)) out.push({ path: p, type: 'removed', oldValue: oldVal[k] })
					else this.deepDiff(oldVal[k], newVal[k], p, out)
				}
				return
			}

			if (Array.isArray(oldVal) && Array.isArray(newVal)) {
				const len = Math.max(oldVal.length, newVal.length)
				for (let i = 0; i < len; i++) {
					const p = path + '[' + i + ']'
					if (i >= oldVal.length) out.push({ path: p, type: 'added', newValue: newVal[i] })
					else if (i >= newVal.length) out.push({ path: p, type: 'removed', oldValue: oldVal[i] })
					else this.deepDiff(oldVal[i], newVal[i], p, out)
				}
				return
			}

			// Leaf (or mismatched types) — record only when actually different.
			if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
				out.push({ path, type: 'changed', oldValue: oldVal, newValue: newVal })
			}
		},

		stringifyDiffValue(value) {
			if (value === null || value === undefined) return '—'
			if (typeof value === 'object') return JSON.stringify(value, null, 2)
			return String(value)
		},

		// ── Inline text diff ──────────────────────────────────────────
		// For updated string values (page content, css...), diff word by
		// word so the modal highlights ONLY what was added/removed instead
		// of dumping both full blobs. Long unchanged runs collapse to "⋯"
		// with a few words of context around each change.
		buildTextDiffs(activity) {
			const out = {}
			const p = activity.properties
			if (activity.event !== 'updated' || !this.hasChanges(p)) return out

			for (const key of this.getChangedKeys(p)) {
				if (this.jsonDiffs[key]) continue
				const oldV = p.old[key]
				const newV = p.attributes[key]
				if (typeof oldV !== 'string' || typeof newV !== 'string') continue

				const htmlDiff = this.inlineTextDiff(oldV, newV)
				if (!htmlDiff) continue

				const entry = { html: htmlDiff, text: null, mode: 'html', textIdentical: false }

				// HTML values (page/news content...) additionally get a
				// parsed-text diff — compares only the visible text, without
				// the markup noise. Defaults to text mode; when the visible
				// text is identical the change is markup-only, so stay on
				// HTML mode and say why.
				const looksHtml = /<[a-z][^>]*>/i.test(oldV) || /<[a-z][^>]*>/i.test(newV)
				if (looksHtml) {
					const oldText = this.stripHtml(oldV)
					const newText = this.stripHtml(newV)
					if (oldText === newText) {
						entry.textIdentical = true
					} else {
						const textDiff = this.inlineTextDiff(oldText, newText)
						if (textDiff) {
							entry.text = textDiff
							entry.mode = 'text'
						}
					}
				}

				out[key] = entry
			}
			return out
		},

		activeDiff(key) {
			const entry = this.textDiffs[key]
			return entry.mode === 'text' && entry.text ? entry.text : entry.html
		},

		// Visible text of an HTML string. DOMParser neither runs scripts nor
		// loads resources, so arbitrary stored markup is safe to parse here.
		// Closing block tags become newlines so paragraphs don't glue together.
		stripHtml(html) {
			const spaced = String(html).replace(/<(br|\/p|\/div|\/h[1-6]|\/li|\/tr|\/table)\b[^>]*>/gi, '$&\n')
			const doc = new DOMParser().parseFromString(spaced, 'text/html')
			const text = (doc.body && doc.body.textContent) || ''
			return text.replace(/[ \t]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim()
		},

		inlineTextDiff(oldStr, newStr) {
			const a = oldStr.split(/(\s+)/).filter(t => t !== '')
			const b = newStr.split(/(\s+)/).filter(t => t !== '')

			// Trim the common prefix/suffix first — typical edits are
			// localized, so this keeps the LCS below tiny.
			let pre = 0
			while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++
			let suf = 0
			while (suf < a.length - pre && suf < b.length - pre && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++

			const ma = a.slice(pre, a.length - suf)
			const mb = b.slice(pre, b.length - suf)

			// Safety cap: edits scattered across a huge document would make
			// the LCS table too big — fall back to the plain boxes.
			if (ma.length * mb.length > 4000000) return null

			// LCS over the middle tokens.
			const n = ma.length
			const m = mb.length
			const w = m + 1
			const dp = new Uint32Array((n + 1) * w)
			for (let i = n - 1; i >= 0; i--) {
				for (let j = m - 1; j >= 0; j--) {
					dp[i * w + j] = ma[i] === mb[j]
						? dp[(i + 1) * w + j + 1] + 1
						: Math.max(dp[(i + 1) * w + j], dp[i * w + j + 1])
				}
			}

			const ops = []
			for (let k = 0; k < pre; k++) ops.push(['same', a[k]])
			let i = 0
			let j = 0
			while (i < n && j < m) {
				if (ma[i] === mb[j]) {
					ops.push(['same', ma[i]])
					i++
					j++
				} else if (dp[(i + 1) * w + j] >= dp[i * w + j + 1]) {
					ops.push(['del', ma[i]])
					i++
				} else {
					ops.push(['add', mb[j]])
					j++
				}
			}
			while (i < n) ops.push(['del', ma[i++]])
			while (j < m) ops.push(['add', mb[j++]])
			for (let k = a.length - suf; k < a.length; k++) ops.push(['same', a[k]])

			return this.renderDiffHtml(ops)
		},

		renderDiffHtml(ops) {
			const CONTEXT = 14
			const MIN_COLLAPSE = 34
			const skip = '<span class="tok-skip"> ⋯ </span>'
			let oldHtml = ''
			let newHtml = ''

			const pushSame = tokens => {
				const h = this.escapeHtml(tokens.join(''))
				oldHtml += h
				newHtml += h
			}
			const pushSkip = () => {
				oldHtml += skip
				newHtml += skip
			}

			let k = 0
			while (k < ops.length) {
				const type = ops[k][0]
				let end = k
				while (end < ops.length && ops[end][0] === type) end++
				const tokens = ops.slice(k, end).map(o => o[1])

				if (type === 'same') {
					if (tokens.length > MIN_COLLAPSE) {
						const isFirst = k === 0
						const isLast = end === ops.length
						if (isFirst) {
							pushSkip()
							pushSame(tokens.slice(-CONTEXT))
						} else if (isLast) {
							pushSame(tokens.slice(0, CONTEXT))
							pushSkip()
						} else {
							pushSame(tokens.slice(0, CONTEXT))
							pushSkip()
							pushSame(tokens.slice(-CONTEXT))
						}
					} else {
						pushSame(tokens)
					}
				} else if (type === 'del') {
					oldHtml += '<mark class="tok-removed">' + this.escapeHtml(tokens.join('')) + '</mark>'
				} else {
					newHtml += '<mark class="tok-added">' + this.escapeHtml(tokens.join('')) + '</mark>'
				}
				k = end
			}

			return { oldHtml, newHtml }
		},

		escapeHtml(str) {
			return String(str)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
		},

		goToPage(page) {
			if (this.loading || page < 1 || page > this.lastPage) return
			this.page = page
			this.getActivities()
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},

		actionClass(event) {
			return {
				'act-badge--created': event === 'created',
				'act-badge--updated': event === 'updated',
				'act-badge--deleted': event === 'deleted' }
		},

		getSubjectTypeName(type) {
			if (!type) return ''
			const parts = type.split('\\')
			return parts[parts.length - 1]
		},

		formatPropertyKey(key) {
			// Special handling for Option model
			if (key === 'meta_key') return 'Option Name'
			if (key === 'meta_value') return 'Option Value'

			return key
				.replace(/_/g, ' ')
				.replace(/\b\w/g, char => char.toUpperCase())
		},

		formatPropertyValue(value, key = null, activity = null) {
			if (value === null || value === undefined) return '—'
			if (typeof value === 'boolean') return value ? 'Yes' : 'No'

			// Special handling for Option model meta_value
			if (activity && activity.subject_type === 'App\\Models\\Option' && key === 'meta_value') {
				return this.formatOptionValue(value, activity)
			}

			if (typeof value === 'object') return JSON.stringify(value, null, 2)

			// Values that hold serialized JSON read much better pretty-printed.
			if (typeof value === 'string' && /^[[{]/.test(value.trim())) {
				try {
					return JSON.stringify(JSON.parse(value), null, 2)
				} catch (e) {
					// not JSON after all — fall through
				}
			}

			return String(value)
		},

		formatOptionValue(value, activity) {
			// Get the meta_key to understand what this option is
			const metaKey = activity.properties?.attributes?.meta_key || activity.properties?.old?.meta_key

			// Try to parse if it's JSON
			let parsedValue = value
			try {
				parsedValue = JSON.parse(value)
			} catch (e) {
				// Not JSON, use as is
			}

			// Known option mappings
			const optionMappings = {
				// Theme options
				'theme_active': {
					'default': 'Maverick (Default)',
					'custom': 'Custom Theme',
					'business': 'Business Theme' },
				// Boolean options
				'maintenance_mode': {
					'0': 'Off',
					'1': 'On',
					false: 'Off',
					true: 'On' },
				'staging': {
					'0': 'Live',
					'1': 'Staging',
					false: 'Live',
					true: 'Staging' },
				'department_source_heygov': {
					'0': 'Disabled',
					'1': 'Enabled',
					false: 'Disabled',
					true: 'Enabled' },
				'department_single_use_quick_menu': {
					'0': 'Disabled',
					'1': 'Enabled',
					false: 'Disabled',
					true: 'Enabled' },
				// Status options
				'status': {
					'live': 'Live',
					'preparing': 'Preparing',
					'abandoned': 'Abandoned',
					'deleted': 'Deleted',
					'development': 'Development' } }

			// If we have a mapping for this meta_key, use it
			if (metaKey && optionMappings[metaKey]) {
				const mapping = optionMappings[metaKey]
				if (mapping[parsedValue]) {
					return mapping[parsedValue]
				}
			}

			// For complex objects, show formatted JSON
			if (typeof parsedValue === 'object') {
				return JSON.stringify(parsedValue, null, 2)
			}

			return String(parsedValue)
		},

		getOptionContext(activity) {
			// Get meta_key from either old or new attributes
			const metaKey = activity.properties?.attributes?.meta_key || activity.properties?.old?.meta_key

			if (!metaKey) return null

			// Provide human-readable description for common options
			const descriptions = {
				'theme_active': 'Website Theme',
				'maintenance_mode': 'Maintenance Mode',
				'staging': 'Website Mode',
				'google_analytic_code': 'Google Analytics Code',
				'heygov_api_url': 'HeyGov API URL',
				'heygov_jurisdiction': 'HeyGov Jurisdiction',
				'heygov_api_key': 'HeyGov API Key',
				'department_source_heygov': 'Department Source - HeyGov Integration',
				'department_single_use_quick_menu': 'Department Single Use Quick Menu',
				'mailer': 'Email Service Provider',
				'mailer_sendgrid_key': 'SendGrid API Key',
				'mailer_mailgun_key': 'Mailgun API Key',
				'mailer_mailgun_domain': 'Mailgun Domain' }

			return descriptions[metaKey] || metaKey
		},

		getRelativeTime(date) {
			if (!date) return ''
			const now = new Date()
			const past = new Date(date)
			const diffInSeconds = Math.floor((now - past) / 1000)

			if (diffInSeconds < 60) return 'Just now'
			if (diffInSeconds < 3600) return Math.floor(diffInSeconds / 60) + ' minutes ago'
			if (diffInSeconds < 86400) return Math.floor(diffInSeconds / 3600) + ' hours ago'
			if (diffInSeconds < 604800) return Math.floor(diffInSeconds / 86400) + ' days ago'
			return this.formatDate(date)
		},

		formatDate(date) {
			if (!date) return ''
			const d = new Date(date)
			return d.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit' })
		},

		hasChanges(properties) {
			return properties && properties.old && properties.attributes && Object.keys(properties.old).length > 0
		},

		getChangedKeys(properties) {
			if (!this.hasChanges(properties)) return []
			return Object.keys(properties.old).filter(key => {
				return properties.old[key] !== properties.attributes[key]
			})
		},

		exportActivities() {
			const params = this.buildFilterParams()

			// Create CSV content
			let csv = 'ID,Event,Client,User,User Email,Source,IP Address,Device,Subject Type,Subject ID,Date\n'

			// We need to fetch all activities for export (not just current page)
			juniperApi
				.get(this.apiBase, { params: { ...params, per_page: 1000 } })
				.then(({ data }) => {
					data.data.forEach(activity => {
						const userName = activity.causer ? activity.causer.name : 'System'
						const userEmail = activity.causer ? activity.causer.email : ''
						const subjectType = this.getSubjectTypeName(activity.subject_type)
						const date = this.formatDate(activity.created_at)

						const device = this.deviceLabel(activity.device)

						const clientName = activity.client ? activity.client.name : this.client.name || ''

						csv += `${activity.id},${activity.event},"${clientName}","${userName}","${userEmail}",${activity.source || ''},${activity.ip_address || ''},"${device}",${subjectType},${activity.subject_id},"${date}"\n`
					})

					// Download CSV
					const blob = new Blob([csv], { type: 'text/csv' })
					const url = window.URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = `${this.isGlobal ? 'all-clients' : this.client.name}-activities-${new Date().toISOString().split('T')[0]}.csv`
					link.click()
					window.URL.revokeObjectURL(url)

					this.$swal.fire({
						icon: 'success',
						title: 'Activities exported successfully',
						timer: 2000 })
				})
				.catch(error => {
					console.log(error)
					this.$swal.fire({
						icon: 'error',
						title: 'Error exporting activities',
						timer: 2000 })
				})
		} } }
</script>

<style scoped>
/* Developer font — used by the details modal (jdbg diff view) */
.jdbg-modal {
	--jdbg-mono: 'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace;
}

/* Toolbar — action pills + subject select + date range + search (mirrors /activities) */
.act-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 1rem;
}

.act-pills {
	display: flex;
	gap: 0;
	background: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.act-pill {
	background: none;
	border: none;
	padding: 5px 14px;
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.15s ease;
}

.act-pill.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.act-pill:hover:not(.active) {
	color: #374151;
}

.act-toolbar__right {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.act-select,
.act-date {
	font-size: 13px;
	padding: 5px 8px;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	color: #374151;
	background: #fff;
	outline: none;
}

.act-select:focus,
.act-date:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}

.act-date-sep {
	color: #8a909c;
}

.act-search {
	position: relative;
	width: 220px;
}

.act-search__icon {
	position: absolute;
	left: 11px;
	top: 50%;
	transform: translateY(-50%);
	color: #8a909c;
	font-size: 12px;
}

.act-search__input {
	width: 100%;
	padding: 5px 28px 5px 30px;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	font-size: 13px;
	outline: none;
}

.act-search__input:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}

.act-search__clear {
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	border: none;
	background: none;
	color: #8a909c;
	cursor: pointer;
	padding: 0;
}

.act-icon-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	background: #fff;
	color: #8a909c;
	cursor: pointer;
	transition: all 0.15s ease;
}

.act-icon-btn:hover {
	color: #2b303b;
	border-color: #c8ccd4;
}

.act-icon-btn--danger:hover {
	color: #dc3545;
	border-color: #f1c2c7;
}

.act-client {
	max-width: 220px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Log settings modal (Logs page) */
.act-settings {
	width: min(560px, 100%);
}

.act-settings__body {
	padding: 4px 16px 16px;
}

.act-settings__section {
	padding: 12px 0;
	border-bottom: 1px solid #f1f3f5;
}

.act-settings__section:last-child {
	border-bottom: none;
}

.act-settings__title {
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 2px;
}

.act-settings__help {
	font-size: 12px;
	margin-bottom: 10px;
}

.act-settings__row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	font-size: 13px;
}

/* Flat small button (settings language: gh-save-btn--sm) */
.act-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	border: 1px solid #e6e8ec;
	background: transparent;
	color: #111827;
}

.act-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.act-btn:disabled {
	opacity: 0.6;
}

.act-btn--danger {
	color: #dc3545;
	border-color: #f1c2c7;
}

.act-btn--danger:hover:not(:disabled) {
	background: #fdf2f3;
}

/* Card + compact flat table (settings language) */
.ada-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 1rem 1.1rem;
	background: transparent;
}

.act-empty {
	text-align: center;
	padding: 1.5rem 1rem;
	color: #8a909c;
	font-size: 0.9rem;
}

.act-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}

.act-table thead th {
	text-align: left;
	font-weight: 600;
	color: #6c757d;
	padding: 4px 10px 8px 0;
	border-bottom: 1px solid #dee2e6;
}

.act-table tbody td {
	padding: 7px 10px 7px 0;
	border-bottom: 1px solid #f1f3f5;
	vertical-align: middle;
	color: #2b303b;
}

.act-table tbody tr:last-child td {
	border-bottom: none;
}

.act-row {
	cursor: pointer;
}

.act-row:hover td {
	background: #f3f5f9;
}

.act-col-id {
	width: 4rem;
}

.act-muted,
.cell-muted {
	color: #8a909c;
}

.act-user {
	font-weight: 600;
}

.act-email {
	margin-left: 0.4rem;
	font-size: 12px;
}

.act-origin {
	font-size: 12px;
	white-space: nowrap;
}

.act-origin-ua {
	font-size: 11px;
	word-break: break-all;
}

.act-subject-id {
	color: #8a909c;
	font-size: 12px;
	margin-left: 0.15rem;
}

.act-badge {
	display: inline-block;
	padding: 1px 9px;
	font-size: 11px;
	font-weight: 700;
	border-radius: 999px;
	text-transform: capitalize;
}

.act-badge--created {
	background: #d1e7dd;
	color: #0f5132;
}

.act-badge--updated {
	background: #e7f1ff;
	color: #0b5ed7;
}

.act-badge--deleted {
	background: #f8d7da;
	color: #842029;
}

/* Pagination — info on the left, prev/next on the right */
.act-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.85rem 0 0.25rem;
	font-size: 0.82rem;
}

.act-pagination__nav {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.act-page-link {
	background: none;
	border: 0;
	padding: 0 4px;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	color: #8a909c;
	cursor: pointer;
	transition: color 0.12s ease;
}

.act-page-link:hover:not(:disabled) {
	color: #2b303b;
}

.act-page-link.active {
	color: #2b303b;
	font-weight: 700;
}

.act-page-link:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}

.act-page-link--arrow {
	font-size: 0.72rem;
}

.act-page-dots {
	color: #8a909c;
	padding: 0 2px;
	user-select: none;
}

/* Loading overlay — table stays visible but blurred underneath */
.act-table-wrap {
	position: relative;
}

.act-table-blurred {
	filter: blur(2px);
	opacity: 0.55;
	pointer-events: none;
	transition: filter 0.15s ease, opacity 0.15s ease;
}

.act-loading-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;
	font-weight: 600;
	color: #2b303b;
}

/* Event badge — pill palette matching the debugging level badges */
.event-badge {
	font-family: var(--jdbg-mono);
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
	display: inline-block;
}

.event-badge.created {
	background-color: #dcfce7;
	color: #166534;
}

.event-badge.updated {
	background-color: #dbeafe;
	color: #1e40af;
}

.event-badge.deleted {
	background-color: #fee2e2;
	color: #991b1b;
}

/* Details modal — same as debugging */
.jdbg-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
	z-index: 1050;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
}

.jdbg-modal {
	background: #ffffff;
	border-radius: 12px;
	width: min(1100px, 100%);
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.jdbg-modal-head {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	padding: 14px 16px;
	border-bottom: 1px solid #f3f4f6;
	font-family: var(--jdbg-mono);
}

.jdbg-modal-sub {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

.jdbg-modal-body {
	padding: 14px 16px;
	overflow-y: auto;
	font-family: var(--jdbg-mono);
}

.jdbg-section-title {
	font-family: var(--jdbg-mono);
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin: 12px 0 6px;
}

/* Option context banner */
.option-context {
	display: flex;
	align-items: center;
	font-size: 13px;
	color: #1e40af;
	background: #eff6ff;
	border: 1px solid #bfdbfe;
	border-radius: 10px;
	padding: 8px 12px;
	margin-bottom: 4px;
}

/* Property rows (created / deleted / changes) */
.property-row {
	padding: 6px 0;
	border-bottom: 1px solid #f9fafb;
}

.property-row:last-of-type {
	border-bottom: none;
}

.property-key {
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 4px;
}

.property-value {
	font-family: var(--jdbg-mono);
	font-size: 12px;
	color: #111827;
	background: #f9fafb;
	border: 1px solid #f3f4f6;
	border-radius: 8px;
	padding: 6px 10px;
	margin: 0;
	white-space: pre-wrap;
	word-break: break-word;
	max-height: 30vh;
	overflow-y: auto;
}

.property-value--new {
	background: #f0fdf4;
	border-color: #dcfce7;
}

.property-value--old {
	background: #fef2f2;
	border-color: #fee2e2;
}

/* JSON deep diff — one row per changed path inside the json */
.json-diff {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.diff-path {
	display: flex;
	align-items: center;
	gap: 6px;
	font-family: var(--jdbg-mono);
	font-size: 12px;
	color: #374151;
	margin-bottom: 4px;
	word-break: break-all;
}

.diff-path code {
	color: #111827;
	background: #f3f4f6;
	border-radius: 6px;
	padding: 1px 6px;
}

.diff-type-badge {
	font-family: var(--jdbg-mono);
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	flex-shrink: 0;
}

.diff-type-badge.added {
	background-color: #dcfce7;
	color: #166534;
}

.diff-type-badge.removed {
	background-color: #fee2e2;
	color: #991b1b;
}

.diff-type-badge.changed {
	background-color: #fef3c7;
	color: #92400e;
}

/* Text/HTML diff mode toggle */
.diff-mode-toggle {
	display: flex;
	align-items: center;
	gap: 4px;
	margin-bottom: 6px;
}

.diff-mode-btn {
	font-family: var(--jdbg-mono);
	font-size: 11px;
	font-weight: 600;
	padding: 3px 10px;
	border: 1px solid #d1d5db;
	border-radius: 12px;
	background: #ffffff;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s ease;
}

.diff-mode-btn:hover {
	color: #111827;
}

.diff-mode-btn.active {
	background: #111827;
	border-color: #111827;
	color: #ffffff;
}

.diff-mode-note {
	font-size: 11px;
	margin-left: 6px;
}

/* Inline text diff highlights */
.property-value >>> .tok-removed {
	background: #fecaca;
	color: #7f1d1d;
	border-radius: 3px;
	padding: 0 1px;
}

.property-value >>> .tok-added {
	background: #bbf7d0;
	color: #14532d;
	border-radius: 3px;
	padding: 0 1px;
}

.property-value >>> .tok-skip {
	color: #9ca3af;
	user-select: none;
}

/* Before → After comparison */
.change-comparison {
	display: grid;
	grid-template-columns: 1fr 24px 1fr;
	gap: 8px;
	align-items: start;
}

.change-arrow {
	display: flex;
	align-items: center;
	justify-content: center;
	color: #9ca3af;
	padding-top: 8px;
}

@media (max-width: 800px) {
	.change-comparison {
		grid-template-columns: 1fr;
	}
	.change-arrow {
		transform: rotate(90deg);
		padding: 0;
	}
}
</style>
